export const supportedDisplayableDatasetTypes = [
  "application/xml",
  "application/json",
  "text/csv",
] as const;

export type DisplayableDatasetTypes =
  (typeof supportedDisplayableDatasetTypes)[number];

const escapeXmlAttribute = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeXmlText = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const formatXmlNode = (node: Node, depth: number): string => {
  const indent = "  ".repeat(depth);

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim();
    return text ? `${indent}${escapeXmlText(text)}` : "";
  }

  if (node.nodeType === Node.CDATA_SECTION_NODE) {
    return `${indent}<![CDATA[${node.textContent ?? ""}]]>`;
  }

  if (node.nodeType === Node.COMMENT_NODE) {
    return `${indent}<!--${node.textContent ?? ""}-->`;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as Element;
  const attributes = Array.from(element.attributes)
    .map(
      (attribute) =>
        `${attribute.name}="${escapeXmlAttribute(attribute.value)}"`,
    )
    .join(" ");
  const openingTag = attributes
    ? `<${element.tagName} ${attributes}>`
    : `<${element.tagName}>`;

  const children = Array.from(element.childNodes)
    .map((childNode) => formatXmlNode(childNode, depth + 1))
    .filter(Boolean);

  if (children.length === 0) {
    return `${indent}${openingTag}</${element.tagName}>`;
  }

  if (
    children.length === 1 &&
    element.childNodes[0]?.nodeType === Node.TEXT_NODE
  ) {
    return `${indent}${openingTag}${children[0].trim()}</${element.tagName}>`;
  }

  return [
    `${indent}${openingTag}`,
    ...children,
    `${indent}</${element.tagName}>`,
  ].join("\n");
};

const prettifyXml = (text: string) => {
  const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
  return formatXmlNode(xmlDoc.documentElement, 0);
};

const prettifyJson = (text: string) =>
  JSON.stringify(JSON.parse(text), null, 2);

const prettifyCsv = (text: string) => {
  let inQuotes = false;
  let valueStart = 0;
  const rows: string[][] = [[]];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (inQuotes) continue;
    else if (c === "," || c === "\n") {
      rows[rows.length - 1].push(text.substring(valueStart, i));
      valueStart = i + 1;
      if (c === "\n") rows.push([]);
    }
  }
  rows[rows.length - 1].push(text.substring(valueStart));
  const lengths = [];
  for (let j = 0; j < rows[0].length; j++) {
    let maxLength = 0;
    for (let i = 0; i < rows.length; i++) {
      maxLength = Math.max(rows[i][j].length, maxLength);
    }
    lengths.push(maxLength);
  }
  for (let i = 0; i < rows.length; i++) {
    for (let j = 0; j < rows[0].length; j++) {
      rows[i][j] = rows[i][j].padEnd(lengths[j]);
    }
  }
  return rows.map((row) => row.join(",")).join("\n");
};

const prettifyFunctions: Record<
  DisplayableDatasetTypes,
  (text: string) => string
> = {
  "application/xml": prettifyXml,
  "application/json": prettifyJson,
  "text/csv": prettifyCsv,
};

export const formatStructuredText = (
  text: string,
  mimeType: DisplayableDatasetTypes,
) => {
  try {
    return prettifyFunctions[mimeType](text);
  } catch {
    return undefined;
  }
};
